<template>
  <div class="glass-container">
    <div class="gift-card-batch">
      <!-- Main Container -->
      <b-container fluid class="main-container">
        <!-- Header -->
        <b-row>
          <b-col>
            <div class="modern-card header-card">
              <div class="header-content">
                <div class="header-left">
                  <div class="icon-wrapper">
                    <b-icon-box class="title-icon" />
                  </div>
                  <div>
                    <h3 class="page-title">Gift Card Batch</h3>
                    <p class="page-subtitle">View batch details and all cards in this batch</p>
                  </div>
                </div>
                <div class="header-stats" v-if="batch">
                  <div class="stat-chip animate-slide-up" style="animation-delay: 0s;">
                    <b-icon-card-list class="stat-icon" />
                    <span class="chip-label">Cards</span>
                    <span class="chip-value">{{ batch.number_of_cards }}</span>
                  </div>
                  <div class="stat-chip animate-slide-up" style="animation-delay: 0.1s;">
                    <b-icon-cash class="stat-icon" />
                    <span class="chip-label">Total</span>
                    <span class="chip-value">${{ formatNumber(batch.total_amount) }}</span>
                  </div>
                  <div class="stat-chip animate-slide-up" style="animation-delay: 0.2s;">
                    <b-icon-tag class="stat-icon" />
                    <span class="chip-label">Per Card</span>
                    <span class="chip-value">${{ formatNumber(batch.per_card_amount) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
        
        <!-- Loading State -->
        <b-row v-if="loading" class="mt-4">
          <b-col class="text-center py-5">
            <div class="loading-container">
              <b-spinner variant="primary" large />
              <p class="mt-3 text-muted">Loading batch details...</p>
            </div>
          </b-col>
        </b-row>
        
        <!-- Batch Details -->
        <b-row v-else-if="batch" class="mt-4">
          <b-col lg="4" xl="3">
            <b-card class="glass-card info-card" no-body>
              <template #header>
                <div class="card-header-custom">
                  <div class="header-left">
                    <div class="header-icon-wrapper">
                      <b-icon-info-circle class="header-icon" />
                    </div>
                    <h5 class="card-header-title">Batch Information</h5>
                  </div>
                  <b-badge :variant="getStatusVariant(batch.status)" class="status-badge-lg">
                    <b-icon-circle-fill class="status-dot" />
                    {{ batch.status }}
                  </b-badge>
                </div>
              </template>
              
              <b-card-body class="card-body-custom">
                <div class="info-grid">
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-hash class="info-icon" /> Batch ID
                    </span>
                    <span class="info-value">{{ batch.batch_id }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-tag class="info-icon" /> Name
                    </span>
                    <span class="info-value">{{ batch.name || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-file-text class="info-icon" /> Description
                    </span>
                    <span class="info-value">{{ batch.description || 'N/A' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-cash-stack class="info-icon" /> Total Amount
                    </span>
                    <span class="info-value highlight">${{ formatNumber(batch.total_amount) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-card-list class="info-icon" /> Number of Cards
                    </span>
                    <span class="info-value">{{ batch.number_of_cards }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-tag class="info-icon" /> Per Card Amount
                    </span>
                    <span class="info-value">${{ formatNumber(batch.per_card_amount) }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-person class="info-icon" /> Created By
                    </span>
                    <span class="info-value">{{ batch.created_by || 'System' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">
                      <b-icon-calendar3 class="info-icon" /> Created At
                    </span>
                    <span class="info-value">{{ formatDate(batch.created_at) }}</span>
                  </div>
                  <div class="info-item" v-if="batch.valid_until">
                    <span class="info-label">
                      <b-icon-clock class="info-icon" /> Valid Until
                    </span>
                    <span class="info-value">{{ formatDate(batch.valid_until) }}</span>
                  </div>
                </div>
                
                <div class="action-buttons-wrapper mt-3">
                  <b-button variant="primary" size="sm" @click="exportBatch" class="action-btn">
                    <b-icon-download class="mr-1" />
                    Export All
                  </b-button>
                  <b-button variant="info" size="sm" @click="resendEmails" class="action-btn" :disabled="!hasEmailableCards">
                    <b-icon-envelope class="mr-1" />
                    Resend Emails
                  </b-button>
                  <b-button variant="success" size="sm" @click="printBatchCards" class="action-btn">
                    <b-icon-printer class="mr-1" />
                    Print All
                  </b-button>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
          
          <b-col lg="8" xl="9">
            <b-card class="glass-card table-card" no-body>
              <template #header>
                <div class="card-header-custom">
                  <div class="header-left">
                    <div class="header-icon-wrapper">
                      <b-icon-gift class="header-icon" />
                    </div>
                    <h5 class="card-header-title">
                      Cards in Batch ({{ cards.length }})
                    </h5>
                  </div>
                  <div class="header-actions">
                    <b-button
                      variant="outline-secondary"
                      size="sm"
                      @click="loadCards"
                      :disabled="cardsLoading"
                    >
                      <b-spinner small v-if="cardsLoading" />
                      <b-icon-arrow-repeat v-else class="mr-1" />
                      Refresh
                    </b-button>
                  </div>
                </div>
              </template>
              
              <b-card-body class="card-body-custom table-body-wrapper">
                <!-- Fixed height table container with overflow -->
                <div class="table-scroll-container">
                  <b-table
                    :items="cards"
                    :fields="cardFields"
                    striped
                    hover
                    responsive
                    class="glass-table modern-table"
                    :busy="cardsLoading"
                  >
                    <template #table-busy>
                      <div class="text-center py-4">
                        <b-spinner variant="primary" small />
                        <p class="mt-2 text-muted">Loading cards...</p>
                      </div>
                    </template>
                    
                    <template #cell(amount)="data">
                      <span class="amount-value">${{ formatNumber(data.value) }}</span>
                    </template>
                    
                    <template #cell(remaining_balance)="data">
                      <span class="balance-value">${{ formatNumber(data.value) }}</span>
                    </template>
                    
                    <template #cell(status)="data">
                      <b-badge :variant="getStatusVariant(data.value)" class="status-badge">
                        <b-icon-circle-fill class="status-dot" />
                        {{ data.value }}
                      </b-badge>
                    </template>
                    
                    <template #cell(assigned_to_email)="data">
                      <span v-if="data.value" class="email-value">
                        <b-icon-envelope-fill class="email-icon" />
                        {{ data.value }}
                      </span>
                      <span v-else class="text-muted">Unassigned</span>
                    </template>
                    
                    <template #cell(created_at)="data">
                      <span class="date-value">{{ formatDate(data.value) }}</span>
                    </template>
                    
                    <template #cell(actions)="data">
                      <b-button
                        size="sm"
                        variant="outline-info"
                        @click="viewCard(data.item)"
                        class="action-icon-btn"
                        title="View Card"
                      >
                        <b-icon-eye />
                      </b-button>
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="sendSingleEmail(data.item)"
                        :disabled="!data.item.assigned_to_email"
                        class="action-icon-btn"
                        title="Send Email"
                      >
                        <b-icon-envelope />
                      </b-button>
                    </template>
                  </b-table>
                </div>
                
                <!-- Empty State -->
                <div v-if="cards.length === 0 && !cardsLoading" class="empty-state">
                  <div class="empty-state-icon">
                    <b-icon-inbox font-scale="3" />
                  </div>
                  <h3>No Cards Found</h3>
                  <p>This batch doesn't have any cards yet</p>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
        
        <!-- Not Found -->
        <b-row v-else class="mt-4">
          <b-col class="text-center py-5">
            <div class="not-found-container">
              <b-icon-exclamation-triangle scale="3" variant="warning" class="not-found-icon" />
              <h4 class="mt-3">Batch Not Found</h4>
              <p class="text-muted">The batch you're looking for doesn't exist or has been removed.</p>
              <b-button variant="primary" @click="$router.push('/gift-card/generate')" class="mt-2">
                <b-icon-arrow-left class="mr-1" />
                Back to Generator
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!-- Print Modal -->
    <b-modal
      id="batch-print-modal"
      size="xl"
      title="Batch Print Preview"
      hide-footer
      centered
      @hidden="closePrintModal"
      class="glass-modal"
    >
      <div class="batch-print-container" ref="batchPrintContent">
        <div class="batch-print-header">
          <div class="print-header-left">
            <h1 class="print-store-name">
              <b-icon-shop class="mr-2" /> FURNITURE MECCA
            </h1>
            <p class="print-store-address">123 Furniture Lane, City, State 12345</p>
            <p class="print-store-phone">
              <b-icon-telephone class="mr-1" /> Phone: (555) 123-4567 | 
              <b-icon-envelope class="mr-1" /> Email: info@furnituremecca.com
            </p>
          </div>
          <div class="print-header-right">
            <div class="print-batch-badge">
              <b-icon-tag class="mr-1" /> BATCH
            </div>
            <div class="print-batch-id">{{ batch ? batch.batch_id : 'N/A' }}</div>
            <div class="print-batch-date">
              <b-icon-calendar3 class="mr-1" /> 
              Generated: {{ batch && batch.created_at ? formatDateFull(batch.created_at) : 'N/A' }}
            </div>
          </div>
        </div>
        
        <hr class="print-divider" />
        
        <div class="batch-info-grid">
          <div class="info-item">
            <span class="info-label"><b-icon-tag class="mr-1" /> Batch Name:</span>
            <span class="info-value">{{ batch ? batch.name : 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label"><b-icon-percent class="mr-1" /> Discount:</span>
            <span class="info-value">{{ batch ? '$' + formatNumber(batch.per_card_amount) : 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label"><b-icon-card-list class="mr-1" /> Vouchers:</span>
            <span class="info-value">{{ batch ? batch.number_of_cards : 0 }}</span>
          </div>
          <div class="info-item">
            <span class="info-label"><b-icon-circle-fill class="mr-1" /> Status:</span>
            <span class="info-value status-badge-print">{{ batch ? batch.status : 'N/A' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label"><b-icon-person class="mr-1" /> Created By:</span>
            <span class="info-value">{{ batch ? batch.created_by : 'System' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label"><b-icon-calendar3 class="mr-1" /> Valid Until:</span>
            <span class="info-value">{{ batch && batch.valid_until ? formatDate(batch.valid_until) : 'No expiration' }}</span>
          </div>
        </div>
        
        <hr class="print-divider" />
        
        <div class="print-vouchers-grid">
          <div v-if="cards.length === 0" class="text-center py-4">
            <b-icon-inbox scale="2" class="text-muted" />
            <p>No vouchers found in this batch</p>
          </div>
          <div
            v-for="(card, index) in cards"
            :key="index"
            class="print-voucher-item"
          >
            <div class="print-voucher">
              <div class="print-voucher-header">
                <span class="print-voucher-title">FURNITURE MECCA</span>
                <span class="print-voucher-badge"><b-icon-gift-fill class="mr-1" />GIFT VOUCHER</span>
              </div>
              <div class="print-voucher-body">
                <div class="print-voucher-text">{{ batch ? batch.name : 'N/A' }}</div>
                <div class="print-voucher-discount">
                  <span class="print-percent">$</span>{{ formatNumber(card.amount) }}
                </div>
                <div class="print-voucher-text">ON YOUR NEXT PURCHASE</div>
                <div class="print-voucher-code">
                  <b-icon-hash class="mr-1" /> {{ card.code }}
                </div>
              </div>
              <div class="print-voucher-footer">
                <div>
                  <b-icon-calendar3 class="mr-1" /> 
                  {{ card.valid_until ? 'Valid until: ' + formatDate(card.valid_until) : 'No expiration' }}
                </div>
                <div>
                  <b-icon-exclamation-circle class="mr-1" /> ONE TIME USE ONLY
                </div>
                <div>
                  <b-icon-envelope class="mr-1" /> 
                  {{ card.assigned_to_email || 'Unassigned' }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <hr class="print-divider" />
        
        <div class="print-footer">
          <p><b-icon-heart class="mr-1" /> Thank you for choosing Furniture Mecca!</p>
          <p class="print-footer-small">This is a system-generated document. Please verify all information.</p>
          <p class="print-footer-small">© {{ new Date().getFullYear() }} Furniture Mecca. All rights reserved.</p>
        </div>
      </div>
      
      <template #modal-footer>
        <div class="print-modal-footer">
          <b-button variant="secondary" @click="$bvModal.hide('batch-print-modal')">
            <b-icon-x class="mr-1" /> Close
          </b-button>
          <b-button variant="primary" @click="printBatchPreview">
            <b-icon-printer class="mr-1" /> Print
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import moment from 'moment';

export default {
  name: 'GiftCardBatch',
  props: ['batchId'],
  data() {
    return {
      loading: true,
      cardsLoading: false,
      batch: null,
      cards: [],
      cardFields: [
        { key: 'code', label: 'Code', sortable: true },
        { key: 'amount', label: 'Amount', sortable: true },
        { key: 'remaining_balance', label: 'Balance', sortable: true },
        { key: 'assigned_to_email', label: 'Assigned To' },
        { key: 'status', label: 'Status' },
        { key: 'created_at', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-center' }
      ]
    };
  },
  
  computed: {
    hasEmailableCards() {
      return this.cards.some(c => c.assigned_to_email && c.status !== 'redeemed');
    }
  },
  
  created() {
    this.loadBatch();
  },
  
  methods: {
    formatNumber(value) {
      if (value === undefined || value === null) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    
    async loadBatch() {
      this.loading = true;
      
      try {
        const batchResponse = await Requestor.getAsyncP('/api/giftcard/get_batches/', {
          batch_id: this.batchId
        }, this);
        
        if (batchResponse.data.result && batchResponse.data.result.length > 0) {
          this.batch = batchResponse.data.result[0];
          await this.loadCards();
        } else {
          this.batch = null;
        }
      } catch (error) {
        console.error('Error loading batch:', error);
        this.$bvToast.toast('Failed to load batch details', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadCards() {
      this.cardsLoading = true;
      
      try {
        const response = await Requestor.getAsyncP('/api/giftcard/get_gift_cards/', {
          batch_id: this.batchId
        }, this);
        
        if (response.data.result) {
          this.cards = response.data.result;
        }
      } catch (error) {
        console.error('Error loading cards:', error);
        this.$bvToast.toast('Failed to load cards', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      } finally {
        this.cardsLoading = false;
      }
    },
    
    getStatusVariant(status) {
      const variants = {
        'pending': 'secondary',
        'generated': 'info',
        'sent': 'primary',
        'redeemed': 'success',
        'expired': 'danger',
        'cancelled': 'danger',
        'active': 'success'
      };
      return variants[status] || 'secondary';
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('MM/DD/YYYY hh:mm A');
    },
    
    formatDateFull(date) {
      if (!date) return 'N/A';
      return moment(date).format('ddd, DD MMM YYYY HH:mm:ss [GMT]');
    },
    
    viewCard(card) {
      this.$router.push(`/gift-card/view/${card.code}`);
    },
    
    async exportBatch() {
      if (this.cards.length === 0) {
        this.$bvToast.toast('No cards to export', {
          title: 'Info',
          variant: 'warning'
        });
        return;
      }
      
      const headers = ['Code', 'Amount', 'Remaining Balance', 'Status', 'Assigned To', 'Created At'];
      const rows = this.cards.map(card => [
        card.code,
        card.amount,
        card.remaining_balance,
        card.status,
        card.assigned_to_email || 'Unassigned',
        new Date(card.created_at).toLocaleString()
      ]);
      
      let csv = headers.join(',') + '\n';
      rows.forEach(row => {
        csv += row.join(',') + '\n';
      });
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_${this.batch.batch_id}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      this.$bvToast.toast('Export completed successfully', {
        title: 'Success',
        variant: 'success'
      });
    },
    
    printBatchCards() {
      if (this.cards.length === 0) {
        this.$bvToast.toast('No vouchers to print.', { title: 'Info', variant: 'warning' });
        return;
      }
      this.$bvModal.show('batch-print-modal');
    },
    
    printBatchPreview() {
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      const doc = printWindow.document;
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Gift Vouchers - ${this.batch ? this.batch.batch_id : 'N/A'}</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; background: #f0f2f5; }
            .print-container { max-width: 1100px; margin: 0 auto; }
            
            .print-header {
              text-align: center;
              padding: 30px 0 20px;
              border-bottom: 3px solid #2c3e50;
              margin-bottom: 30px;
              background: white;
              border-radius: 12px 12px 0 0;
            }
            .print-header h1 { font-size: 32px; font-weight: 700; color: #2c3e50; letter-spacing: 3px; }
            .print-header .subtitle { color: #7f8c8d; font-size: 14px; margin-top: 4px; }
            .batch-info {
              display: flex;
              justify-content: center;
              gap: 20px;
              margin-top: 12px;
              flex-wrap: wrap;
            }
            .batch-info span {
              background: #e9ecef;
              padding: 4px 16px;
              border-radius: 20px;
              font-size: 13px;
              color: #555;
            }
            
            .voucher-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 25px;
            }
            
            .voucher-print {
              background: #f8f4f0;
              border-radius: 12px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.08);
              border: 1px solid #e8e0d8;
              overflow: hidden;
              break-inside: avoid;
              padding: 28px 24px 20px;
              text-align: center;
              min-height: 280px;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }
            
            .voucher-print-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            }
            .voucher-print-title {
              font-size: 18px;
              font-weight: 700;
              color: #5a4a3a;
              letter-spacing: 2px;
            }
            .voucher-print-badge {
              border: 2px solid #5a4a3a;
              color: #5a4a3a;
              padding: 2px 14px;
              border-radius: 3px;
              font-size: 11px;
              font-weight: 700;
              letter-spacing: 1px;
            }
            
            .voucher-print-body {
              padding: 10px 0;
            }
            .voucher-print-discount {
              font-size: 64px;
              font-weight: 900;
              color: #2c3e50;
              line-height: 1;
            }
            .voucher-print-discount .print-percent {
              font-size: 24px;
              font-weight: 700;
              color: #7f8c8d;
            }
            .voucher-print-text {
              font-size: 15px;
              font-weight: 600;
              color: #6c7a8d;
              letter-spacing: 2px;
              margin-top: 8px;
            }
            .voucher-print-code {
              font-size: 13px;
              font-family: 'Courier New', monospace;
              background: #f0f4f8;
              padding: 4px 16px;
              border-radius: 6px;
              display: inline-block;
              margin-top: 12px;
              letter-spacing: 1px;
              font-weight: 600;
              color: #2c3e50;
              border: 1px dashed #4a90e2;
            }
            
            .voucher-print-footer {
              display: flex;
              justify-content: space-between;
              padding: 12px 0 0;
              border-top: 1px solid #e0d8d0;
              margin-top: 14px;
              font-size: 11px;
              color: #8a7a6a;
              letter-spacing: 0.5px;
              flex-wrap: wrap;
              gap: 4px;
            }
            
            .print-footer {
              text-align: center;
              padding: 25px 0 15px;
              border-top: 2px solid #e8eaed;
              margin-top: 30px;
              background: white;
              border-radius: 0 0 12px 12px;
            }
            .print-footer p { color: #7f8c8d; font-size: 13px; margin: 4px 0; }
            .print-footer .footer-small { font-size: 11px; color: #95a5a6; }
            
            @media print {
              body { padding: 10px; background: white; }
              .voucher-print { box-shadow: none; border: 1px solid #ddd; break-inside: avoid; }
            }
            
            @media (max-width: 768px) {
              .voucher-grid { grid-template-columns: 1fr; }
              .voucher-print-discount { font-size: 52px; }
              .batch-info { flex-direction: column; gap: 6px; align-items: center; }
              .voucher-print-footer { flex-direction: column; gap: 4px; text-align: center; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="print-header">
              <h1>🎁 GIFT VOUCHERS</h1>
              <p class="subtitle">Present this voucher to redeem your discount</p>
              <div class="batch-info">
                <span>📋 ${this.batch ? this.batch.batch_id : 'N/A'}</span>
                <span>📅 ${this.batch && this.batch.created_at ? new Date(this.batch.created_at).toLocaleDateString() : 'N/A'}</span>
                <span>🏷️ ${this.batch ? '$' + this.formatNumber(this.batch.per_card_amount) : 'N/A'}</span>
                <span>📊 ${this.cards.length} vouchers</span>
              </div>
            </div>
            
            <div class="voucher-grid">
      `;
      
      this.cards.forEach(card => {
        const validityText = card.valid_until 
          ? 'Valid until: ' + new Date(card.valid_until).toLocaleDateString() 
          : '♾️ No expiration';
        
        htmlContent += `
          <div class="voucher-print">
            <div class="voucher-print-header">
              <span class="voucher-print-title">🎁 Gift</span>
              <span class="voucher-print-badge">VOUCHER</span>
            </div>
            <div class="voucher-print-body">
              <div class="voucher-print-discount">-${this.formatNumber(card.amount)}<span class="print-percent">$</span></div>
              <div class="voucher-print-text">💫 ON YOUR NEXT PURCHASE</div>
              <div class="voucher-print-code"># ${card.code}</div>
            </div>
            <div class="voucher-print-footer">
              <div>📅 ${validityText}</div>
              <div>📧 ${card.assigned_to_email || 'Unassigned'}</div>
            </div>
          </div>
        `;
      });
      
      htmlContent += `
            </div>
            
            <div class="print-footer">
              <p>❤️ Thank you for choosing Furniture Mecca!</p>
              <p class="footer-small">This is a system-generated document. Please verify all information.</p>
              <p class="footer-small">© ${new Date().getFullYear()} Furniture Mecca. All rights reserved.</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 2000);
            };
          <\/script>
        </body>
        </html>
      `;
      
      doc.write(htmlContent);
      doc.close();
    },
    
    closePrintModal() {
      // Clean up if needed
    },
    
    async resendEmails() {
      const cardsWithEmail = this.cards.filter(c => c.assigned_to_email && c.status !== 'redeemed');
      if (cardsWithEmail.length === 0) {
        this.$bvToast.toast('No cards with email addresses or already redeemed', {
          title: 'Info',
          variant: 'warning'
        });
        return;
      }
      
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Resend emails to ${cardsWithEmail.length} cards?`,
        {
          title: 'Resend Emails',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'RESEND',
          cancelTitle: 'CANCEL'
        }
      );
      
      if (!confirmed) return;
      
      let sent = 0;
      let failed = 0;
      
      for (const card of cardsWithEmail) {
        try {
          await Requestor.postAsyncP('/api/giftcard/send_email/', {
            gift_card: card,
            recipient_email: card.assigned_to_email,
            recipient_name: card.assigned_to_name || 'Valued Customer',
            store_name: 'Furniture Mecca',
            valid_until: card.valid_until
          }, this);
          sent++;
        } catch (error) {
          console.error('Failed to send email:', error);
          failed++;
        }
      }
      
      this.$bvToast.toast(
        `Resent ${sent} emails${failed > 0 ? `, ${failed} failed` : ''}`,
        {
          title: 'Email Status',
          variant: failed > 0 ? 'warning' : 'success'
        }
      );
    },
    
    async sendSingleEmail(card) {
      if (!card.assigned_to_email) {
        this.$bvToast.toast('No email address assigned to this card', {
          title: 'Info',
          variant: 'warning'
        });
        return;
      }
      
      try {
        await Requestor.postAsyncP('/api/giftcard/send_email/', {
          gift_card: card,
          recipient_email: card.assigned_to_email,
          recipient_name: card.assigned_to_name || 'Valued Customer',
          store_name: 'Furniture Mecca',
          valid_until: card.valid_until
        }, this);
        
        this.$bvToast.toast(`Email sent to ${card.assigned_to_email}`, {
          title: 'Success',
          variant: 'success'
        });
      } catch (error) {
        console.error('Failed to send email:', error);
        this.$bvToast.toast('Failed to send email', {
          title: 'Error',
          variant: 'danger'
        });
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   GLASS CONTAINER - Same as other components
   ============================================ */
.glass-container {
  min-height: 100vh;
  padding: 20px;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.glass-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  z-index: 0;
}

.gift-card-batch {
  padding: 0;
  background: transparent;
  min-height: 100vh;
  z-index: 1;
  position: relative;
}

.main-container {
  padding: 24px;
  position: relative;
  z-index: 1;
}

/* ============================================
   MODERN CARD - Same as other components
   ============================================ */
.modern-card {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 25px;
  margin: 0 auto;
  margin-top: 10px;
  max-width: 1800px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.header-card {
  padding: 20px 28px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4a90e2, #667eea);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.page-subtitle {
  font-size: 14px;
  color: #6c7a8d;
  margin: 2px 0 0;
}

.header-stats {
  display: flex;
  gap: 12px;
}

.stat-chip {
  background: rgba(255, 255, 255, 0.8);
  padding: 6px 16px;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  animation: slideUp 0.6s ease forwards;
  opacity: 0;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  opacity: 0;
  animation: slideUp 0.6s ease forwards;
}

.stat-icon { font-size: 14px; color: #4a6cf7; }
.chip-label { color: #6c7a8d; }
.chip-value { font-weight: 700; color: #1a1a2e; }

/* ============================================
   GLASS CARDS
   ============================================ */
.glass-card {
  border: none;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
  height: 100%;
}

.card-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(220, 226, 236, 0.3);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header-icon-wrapper {
  width: 32px;
  height: 32px;
  background: rgba(74, 108, 247, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6cf7;
}

.card-header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.card-body-custom {
  padding: 24px;
}

/* ============================================
   TABLE SCROLL CONTAINER - FIXED HEIGHT
   ============================================ */
.table-body-wrapper {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-scroll-container {
  flex: 1;
  overflow-y: auto;
  max-height: 500px;
  min-height: 300px;
  padding: 0;
}

.table-scroll-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-scroll-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
}

.table-scroll-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #4a90e2, #667eea);
  border-radius: 10px;
}

.table-scroll-container::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #3a7bc8, #5a67d8);
}

/* Table styling */
.glass-table {
  margin-bottom: 0;
  width: 100%;
}

.glass-table >>> thead th {
  background: rgba(244, 247, 252, 0.8) !important;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(8px);
}

.glass-table >>> tbody td {
  padding: 12px 16px;
  vertical-align: middle;
  border-color: rgba(226, 232, 240, 0.3);
}

.glass-table >>> tbody tr {
  background: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.glass-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.6) !important;
}

/* ============================================
   INFO CARD
   ============================================ */
.info-card {
  height: 100%;
}

.status-badge-lg {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  font-size: 8px;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid rgba(220, 226, 236, 0.2);
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 10px;
  text-transform: uppercase;
  color: #8a9aa8;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.info-icon {
  font-size: 12px;
  color: #4a6cf7;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin-top: 2px;
  word-break: break-word;
}

.info-value.highlight {
  color: #4a6cf7;
  font-size: 16px;
}

.action-buttons-wrapper {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  flex: 1;
  min-width: 100px;
  font-size: 13px;
  padding: 6px 12px;
}

/* ============================================
   TABLE STYLES
   ============================================ */
.amount-value,
.balance-value {
  font-weight: 600;
  color: #2d3748;
}

.email-value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #4a5568;
}

.email-icon {
  color: #667eea;
  font-size: 0.9rem;
}

.date-value {
  color: #718096;
  font-size: 0.85rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
  text-transform: capitalize;
}

.action-icon-btn {
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  margin: 0 2px;
}

.action-icon-btn:hover {
  transform: scale(1.1);
}

.action-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ============================================
   LOADING
   ============================================ */
.loading-container {
  text-align: center;
  padding: 60px 20px;
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state-icon {
  color: #a0aec0;
  margin-bottom: 15px;
}

.empty-state h3 {
  color: #2d3748;
  margin-bottom: 8px;
  font-weight: 600;
}

.empty-state p {
  color: #718096;
  margin-bottom: 15px;
}

/* ============================================
   NOT FOUND
   ============================================ */
.not-found-container {
  padding: 60px 20px;
}

.not-found-icon {
  color: #ed8936;
}

/* ============================================
   BATCH PRINT MODAL
   ============================================ */
.batch-print-container {
  padding: 20px;
  background: white;
}

.batch-print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 20px;
  border-bottom: 3px solid #2c3e50;
  margin-bottom: 20px;
}

.print-store-name {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.print-store-address {
  color: #7f8c8d;
  margin: 4px 0;
}

.print-store-phone {
  color: #7f8c8d;
  margin: 4px 0;
}

.print-header-right {
  text-align: right;
}

.print-batch-badge {
  background: #2c3e50;
  color: white;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: inline-block;
}

.print-batch-id {
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin-top: 8px;
}

.print-batch-date {
  color: #7f8c8d;
  font-size: 13px;
  margin-top: 4px;
}

.print-divider {
  border: none;
  border-top: 2px dashed #e0e0e0;
  margin: 20px 0;
}

.batch-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
}

.batch-info-grid .info-item {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  border-bottom: none;
}

.batch-info-grid .info-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #7f8c8d;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.batch-info-grid .info-value {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
}

.status-badge-print {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  background: #d4edda;
  color: #155724;
}

.print-vouchers-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 20px 0;
}

.print-voucher {
  background: #f8f4f0;
  border-radius: 12px;
  padding: 24px 20px 18px;
  border: 1px solid #e8e0d8;
  break-inside: avoid;
  text-align: center;
  min-height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.print-voucher-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.print-voucher-title {
  font-size: 18px;
  font-weight: 700;
  color: #5a4a3a;
  letter-spacing: 2px;
}

.print-voucher-badge {
  border: 1.5px solid #5a4a3a;
  color: #5a4a3a;
  padding: 2px 12px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
}

.print-voucher-body {
  padding: 10px 0;
}

.print-voucher-discount {
  font-size: 52px;
  font-weight: 800;
  color: #2c3e50;
  line-height: 1.1;
}

.print-voucher-discount .print-percent {
  font-size: 45px;
  font-weight: 700;
  color: #7f8c8d;
}

.print-voucher-text {
  font-size: 14px;
  font-weight: 600;
  color: #6c7a8d;
  letter-spacing: 2px;
  margin-top: 6px;
}

.print-voucher-code {
  font-size: 13px;
  font-family: 'Courier New', monospace;
  background: #f0f4f8;
  padding: 4px 16px;
  border-radius: 6px;
  display: inline-block;
  margin-top: 10px;
  letter-spacing: 1px;
  font-weight: 600;
  color: #2c3e50;
  border: 1px dashed #4a90e2;
}

.print-voucher-footer {
  display: flex;
  justify-content: space-between;
  padding: 10px 0 0;
  border-top: 1px solid #e0d8d0;
  margin-top: 12px;
  font-size: 11px;
  color: #8a7a6a;
  flex-wrap: wrap;
  gap: 4px;
}

.print-footer {
  text-align: center;
  padding: 20px 0 10px;
  border-top: 2px solid #e8eaed;
  margin-top: 20px;
}

.print-footer p {
  color: #7f8c8d;
  font-size: 13px;
  margin: 4px 0;
}

.print-footer-small {
  font-size: 11px;
  color: #95a5a6;
}

.print-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 992px) {
  .info-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .batch-info-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .main-container { padding: 16px; }
  .page-title { font-size: 20px; }
  .header-content { flex-direction: column; align-items: flex-start; }
  .header-stats { width: 100%; justify-content: flex-start; flex-wrap: wrap; }
  .stat-chip { flex: 1; min-width: 80px; justify-content: center; }
  .info-grid { grid-template-columns: 1fr; }
  .action-buttons-wrapper { flex-direction: column; }
  .action-btn { min-width: 100%; }
  .batch-info-grid { grid-template-columns: 1fr; }
  .print-vouchers-grid { grid-template-columns: 1fr; }
  .print-voucher-footer { flex-direction: column; gap: 4px; text-align: center; }
  .batch-print-header { flex-direction: column; }
  .print-header-right { text-align: left; margin-top: 10px; }
  .card-header-custom { flex-direction: column; align-items: flex-start; gap: 8px; }
  .header-actions { width: 100%; }
  .table-scroll-container {
    max-height: 350px;
    min-height: 200px;
  }
}

@media (max-width: 480px) {
  .header-stats { flex-direction: column; }
  .stat-chip { width: 100%; justify-content: center; }
  .batch-info-grid { grid-template-columns: 1fr; }
  .table-scroll-container {
    max-height: 300px;
    min-height: 150px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/GiftCard/GiftCardBatch.vue